import {Button, List, message, Modal, notification, Popover, Progress, Space} from "antd";
import React, {useContext, useEffect, useRef, useState, useCallback} from "react";
import SparkMD5 from "spark-md5";
import sha256 from "js-sha256";
import Evaporate from "evaporatejs";
import {AppContext} from "../../contexts/AppContext";
import {useUploadsDispatch, useUploadsState} from "../../contexts/UploadsContext";
import {useAssetsDispatch} from "../../contexts/AssetsContext";
import api from "../api";
import {useAggsDispatch} from "../../contexts/AggsContext";
import useConsumer from "../../channels/consumer";
import {SessionContext} from "../../contexts/SessionContext";
import uuid from "../helpers/uuid";
import mapLimit from 'async/mapLimit';
import {useLoadedAssetsDispatch} from "../../contexts/LoadedAssetsContext";
import PauseOutlined from "@ant-design/icons/lib/icons/PauseOutlined";
import PlayCircleOutlined from "@ant-design/icons/lib/icons/PlayCircleOutlined";
import pluralize from "pluralize";
import {useEditAssetsDispatch, useEditAssetsState} from "../../contexts/EditAssetsContext";

import {
    BrowserView,
    MobileView,
    isBrowser,
    isMobile
} from "device-detect";
import {useAssetGroupDispatch, useAssetGroupState} from "../../contexts/AssetGroupContext";
import {useTranslation} from "react-i18next";
import {FileFilled, OrderedListOutlined} from "@ant-design/icons";
import VerticalSpace from "@/components/helpers/VerticalSpace";
import ListItem from "antd/es/upload/UploadList/ListItem";
import {clearBulkUploadSubscription, createBulkUploadLifecycle} from "./bulkUploadLifecycle";
import {activeUploadGuids, shouldStopForCancellation} from "./uploadTracking";
import useRequestRefreshScheduler from "../helpers/useRequestRefreshScheduler";

export default ({quickUpload})=> {
    const {state} = useContext(AppContext);
    const {jwt} = state

    const {state: sessionState} = useContext(SessionContext);
    const {sessionGuid, currentOrg} = sessionState;

    const {uploads, uploadingAssets, fileAdds, disconnected, uploadState} = useUploadsState();
    const uploadsDispatch = useUploadsDispatch()

    const assetsDispatch = useAssetsDispatch();
    const loadedAssetsDispatch = useLoadedAssetsDispatch()

    const aggsDispatch = useAggsDispatch();

    const editAssetDispatch = useEditAssetsDispatch()

    const lastProgressTimestamps = useRef({});
    const lastStatsRefresh = useRef(0);
    const uploadStateRef = useRef({byGuid: {}});
    const evaporateIdsByUploadGuid = useRef({});
    const cancelledRevisionUploadGuids = useRef(new Set());
    const cancelledRevisionBulkUploadIds = useRef(new Set());
    const STALL_TIMEOUT = 60000; // 60 seconds
    const STATS_REFRESH_THROTTLE = 3000; // Refresh stats at most every 3 seconds

    const {currentUpload} = useAssetGroupState()
    const assetGroupDispatch = useAssetGroupDispatch();

    // Keep uploadStateRef in sync with uploadState
    useEffect(() => {
        if(currentUpload?.guid) uploadStateRef.current.byGuid[currentUpload.guid] = uploadState

        uploadStateRef.current = {
            ...uploadStateRef.current,
            state: uploadState,
            uploadId: currentUpload?.id,
            uploadGuid: currentUpload?.guid
        };
    }, [uploadState, currentUpload?.id, currentUpload?.guid]);

    const stateForUpload = (upload) => {
        const currentState = uploadStateRef.current
        const stateByGuid = upload?.guid && currentState.byGuid[upload.guid]
        const matchesId = upload?.id && currentState.uploadId === upload.id
        const matchesGuid = upload?.guid && currentState.uploadGuid === upload.guid

        if(stateByGuid) return stateByGuid

        return matchesId || matchesGuid ? currentState.state : 'active'
    }

    const uploadStatsRefreshScheduler = useRequestRefreshScheduler(() => {
        if(currentUpload?.guid) {
            return api(`/api/uploads/${currentUpload.guid}`).then(res => {
                assetGroupDispatch({type: 'setCurrentUpload', upload: res.data});
            });
        }
    }, {
        onError: error => console.error('Failed to refresh upload stats:', error),
        debounce: false,
    });

    const refreshUploadStats = () => uploadStatsRefreshScheduler.schedule();

    const onProgress = (asset, percent, stats) => {
        uploadsDispatch({type:'uploadProgress', asset, percent, stats})
        uploadsDispatch({type: 'updateUploadProgress', assetId: asset.id})

        // Track last progress timestamp
        lastProgressTimestamps.current[asset.id] = Date.now()

        // Throttled stats refresh during progress
        const now = Date.now();
        if (now - lastStatsRefresh.current > STATS_REFRESH_THROTTLE) {
            lastStatsRefresh.current = now;
            refreshUploadStats();
        }
    }

    const consumer = useConsumer();
    const sub = useRef();
    const bulkUploadLifecycle = useRef(null);

    useEffect(() => {
        const lifecycle = createBulkUploadLifecycle()
        bulkUploadLifecycle.current = lifecycle

        return () => lifecycle.dispose()
    }, [])

    const uploadAsset = (asset, cb) => {
        const assetUploadState = stateForUpload(asset.upload)
        console.log('uploadAsset called for:', asset.id, 'Upload state:', assetUploadState)

        if (shouldStopForCancellation(assetUploadState, asset.allowed_after_cancellation)) {
            console.log('Upload is cancelled, skipping asset:', asset.id);
            return;
        }

        // If upload is paused, wait and retry
        if (assetUploadState === 'paused') {
            console.log('Upload is paused, waiting before adding asset:', asset.id);
            setTimeout(() => uploadAsset(asset, cb), 1000);
            return;
        }

        console.log('adding to evaporate:', asset)
        const file = _.find(FileQueue[asset.upload.guid], {upload_guid: asset.upload_guid})

        if(!file) return console.log('No matching file in queue', FileQueue);

        const evaporateId = decodeURIComponent(`${Config.uploadBucket}/${asset.s3_upload_key}`)
        evaporateIdsByUploadGuid.current[asset.upload.guid] ||= new Set()
        evaporateIdsByUploadGuid.current[asset.upload.guid].add(evaporateId)

        uploader.current.then((evaporate) => {
            evaporate.add({
                file: file,
                name: asset.s3_upload_key,
                progress: (percent, stats) => onProgress(asset, percent, stats),
                complete: (xhr, awsObjectKey) => {
                    console.log('Complete!', awsObjectKey)
                    refreshUploadStats();
                    cb && cb()
                },
                error: (msg) => {
                    console.log('Evaporate Error:', msg)

                    // Auto-retry on error (only if not paused)
                    setTimeout(() => {
                        // Don't auto-resume if upload is paused or cancelled
                        const retryUploadState = stateForUpload(asset.upload)
                        if (retryUploadState === 'paused' || shouldStopForCancellation(retryUploadState, asset.allowed_after_cancellation)) {
                            console.log('Upload is paused or cancelled, skipping auto-retry for asset:', asset.id)
                            return;
                        }

                        console.log('Auto-retrying upload for asset:', asset.id)
                        Promise.resolve(evaporate.resume(evaporateId)).then(() => {
                            console.log('Auto-resumed after error.')
                        }).catch(err => {
                            console.error('Failed to resume after error:', err)
                            // Mark as stalled if resume fails
                            uploadsDispatch({type: 'markAssetStalled', assetId: asset.id});
                            refreshUploadStats();
                        })
                    }, 3000); // Wait 3 seconds before retry
                },
                paused: () => console.log('Paused'),
                pausing: () => console.log('Pausing'),
                resumed: () => {
                    console.log('Resumed')
                    // Clear stalled status on successful resume
                    uploadsDispatch({type: 'updateUploadProgress', assetId: asset.id})
                },
                cancelled: () => console.log('Cancelled'),
                started: key => {
                    api.post(`/api/assets/started_upload`, {key})
                },
                uploadInitiated: () => {
                    assetsDispatch({type:'updateAsset', asset: {...asset, aasm_state:'uploading'}})
                    refreshUploadStats();
                },
                warn: (msg) => {
                    console.log('Evaporate Warning:', msg)
                }
            }).then(
                () => {
                    evaporateIdsByUploadGuid.current[asset.upload.guid]?.delete(evaporateId)
                    onFinish(asset)
                },
                (reason) => {
                    evaporateIdsByUploadGuid.current[asset.upload.guid]?.delete(evaporateId)
                    console.log('File did not upload successfully:', reason)

                    if(shouldStopForCancellation(stateForUpload(asset.upload), asset.allowed_after_cancellation)) return

                    // Mark as stalled on failure
                    uploadsDispatch({type: 'markAssetStalled', assetId: asset.id});
                    refreshUploadStats();
                }
            );
        });
    }
    // TODO: useEffect for new version uploads to pass to `uploadAsset`

    //--------------------------------
    // Setup ActionCable Subscription:
    //--------------------------------

    const {t} = useTranslation();
    const preparingUploadGuids = useRef(new Set())
    const bulkUploadGuids = useRef({})
    const uploadCounts = useRef({})
    const totalUploads = useRef(0)

    const isUploadTracked = useCallback((uploadGuid) => {
        return Boolean(
            uploadGuid &&
            (preparingUploadGuids.current.has(uploadGuid) || uploadCounts.current[uploadGuid] > 0)
        )
    }, [])

    const clearUploadTracking = useCallback((uploadGuid) => {
        if(!uploadGuid) return

        totalUploads.current = Math.max(0, totalUploads.current - (uploadCounts.current[uploadGuid] || 0))
        delete uploadCounts.current[uploadGuid]
        preparingUploadGuids.current.delete(uploadGuid)
        delete evaporateIdsByUploadGuid.current[uploadGuid]
        cancelledRevisionUploadGuids.current.delete(uploadGuid)
        uploadsDispatch({type: 'clearUploadingAssets', uploadGuid})

        Object.keys(bulkUploadGuids.current).forEach(id => {
            if(bulkUploadGuids.current[id] === uploadGuid) {
                bulkUploadLifecycle.current?.cancel(Number(id))
                cancelledRevisionBulkUploadIds.current.delete(Number(id))
                delete bulkUploadGuids.current[id]
            }
        })
    }, [uploadsDispatch])

    const onError = (data, lifecycle = bulkUploadLifecycle.current)=> {
        if(!lifecycle.claimError(data.id)) return

        const uploadGuid = bulkUploadGuids.current[data.id]
        preparingUploadGuids.current.delete(uploadGuid)
        cancelledRevisionBulkUploadIds.current.delete(data.id)
        cancelledRevisionUploadGuids.current.delete(uploadGuid)
        delete bulkUploadGuids.current[data.id]

        message.error({
            content: data.message || t('bulk-upload-prepare-error', 'No uploadable files could be prepared.'),
            duration: 8
        })
    }

    const onReady = (data, source, lifecycle = bulkUploadLifecycle.current)=> {
        if(!lifecycle.claimReady(data.id)) return

        const uploadGuid = bulkUploadGuids.current[data.id]
        const cancelledRevision = cancelledRevisionBulkUploadIds.current.has(data.id)

        if(shouldStopForCancellation(stateForUpload({guid: uploadGuid}), cancelledRevision)) {
            clearUploadTracking(uploadGuid)
            return
        }

        // onReady {type: "ready", id: 1305, total: 0} cable
        console.log('onReady', data, source)

        if(data.existing > 0) {
            notification.warning({
                message: t('existing-assets-skipped-message', 'Skipping existing Assets'),
                description: (
                    <VerticalSpace>
                        <div>{`${data.existing} ${t('existing-assets-notice', 'Assets already present in Upload, skipping.')}`}</div>
                        <ExistingAssetsModal id={data.id}/>
                    </VerticalSpace>
                ),
                duration: 0
            })
        }

        const per_page = 50

        let assets = []
        const getPage = (page)=> {
            if(!lifecycle.isReady(data.id)) return

            api(`/api/bulk_uploads/${data.id}?page=${page}&per_page=${per_page}`).then(res => {
                if(!lifecycle.isReady(data.id)) return

                const pageUploadGuid = res.data[0]?.upload?.guid || uploadGuid

                if(shouldStopForCancellation(stateForUpload({guid: pageUploadGuid}), cancelledRevision)) {
                    clearUploadTracking(pageUploadGuid)
                    return
                }

                if(res.data.status === 'processing') {
                    const retryTimeout = setTimeout(() => {
                        lifecycle.releaseRetryTimeout(data.id, retryTimeout)
                        getPage(1)
                    }, 500)
                    lifecycle.trackRetryTimeout(data.id, retryTimeout)
                    return
                }

                assets = assets.concat(res.data)
                totalUploads.current += res.data.length
                uploadCounts.current[pageUploadGuid] = (uploadCounts.current[pageUploadGuid] || 0) + res.data.length

                assetsDispatch({type: 'assetsAdded', assets: res.data});
                loadedAssetsDispatch({type: 'assetsAdded', assets: res.data}); // IDs only

                uploadsDispatch({type:'addUploadingAssets', assets: res.data})

                if(res.headers['total-pages'] > page) {
                    getPage(page + 1)

                } else {
                    lifecycle.completeReady(data.id)
                    preparingUploadGuids.current.delete(pageUploadGuid)
                    cancelledRevisionBulkUploadIds.current.delete(data.id)
                    delete bulkUploadGuids.current[data.id]
                    mapLimit(assets, 5, uploadAsset)
                }
            })
        }

        aggsDispatch({type:'setTotal', total: data.total})

        getPage(1)
    }

    useEffect(()=>{
        const lifecycle = bulkUploadLifecycle.current

        if(!sub.current && sessionGuid && currentOrg && consumer && consumer.subscriptions) {
            console.log('connecting to BulkUploadsChannel: ', sessionGuid, currentOrg.id, sub.current)

            sub.current = consumer.subscriptions.create({channel: "BulkUploadsChannel", guid: sessionGuid}, {
                connected: ()=> {
                    console.log('BulkUploadsChannel connected')
                },
                disconnected: ()=> {
                    console.log('BulkUploadsChannel disconnected!')
                },
                received: (data) => {
                    switch(data.type) {
                        case 'ready':
                            console.log('BulkUploadsChannel: ready')
                            onReady(data, 'cable', lifecycle)
                            return

                        case 'error':
                            console.log('BulkUploadsChannel: error')
                            onError(data, lifecycle)
                            return

                        case 'retryUpload':
                            console.log('RETRYING UPLOAD:', data.asset)
                            uploadAsset(data.asset)
                            return

                        case 'progress':
                            if(!lifecycle.isPreparing(data.id)) return

                            lifecycle.setCloseMessage(data.id, message.open({
                                key: `preparing-${data.id}`,
                                type: 'loading',
                                content: <PreparingUploadMessage count={data.total} progress={data.progress}/>,
                                duration: 0
                            }))
                            return
                    }
                }
            });
        }

        return ()=> {
            console.log('UploadManager unloaded')
            clearBulkUploadSubscription(consumer, sub)
        }
    }, [currentOrg?.id, consumer])

    //---------------------
    // Prepare Bulk Upload:
    //---------------------

    useEffect(()=>{
        const lifecycle = bulkUploadLifecycle.current

        if(disconnected) {
            message.error('Please wait until reconnected before uploading');
            return
        }

        // POST entire file list to api w/ upload_guid, queue background job, send asset records to front end via cable:
        Object.keys(uploads).map((guid)=> {
            if(guid == 'new') return;

            const files = uploads[guid]

            uploadsDispatch({type:'filesProcessed', files, guid})

            const assets = _.compact(files.map(file => {
                // .filename can be manually set to override; e.g. <DokaButton/>

                // Replace narrow-space character for S3 / CloudFront compatibility:
                let filename = (file.filename || file.name).replace(/\u202F/g, ' ')
                let path = file.webkitRelativePath

                if(filename.match(/\//)) {
                    filename = _.last(file.name.split(/\//));
                    path = file.name
                }

                if(file.upload_guid || file.size == 0 || filename.match(/(\/|^)\./) || path.match(/(\/|^)\./)) return;

                file.upload_guid = uuid()

                return {
                    filename, path, file_size: file.size, upload_guid: file.upload_guid,
                    new_version: file.new_version, asset_id: file.asset_id
                };
            }))

            if(assets.length) {
                preparingUploadGuids.current.add(guid)

                const blob = new Blob([JSON.stringify(assets)], {
                    type: 'application/json'
                });
                const formData = new FormData()
                formData.append('assets', blob)

                api.post(`/api/uploads/${guid}/assets?session_guid=${sessionGuid}`, formData, {
                    headers: {'Content-Type': 'multipart/form-data'}
                }).then(res => {
                    const id = res.data.bulk_upload_id
                    bulkUploadGuids.current[id] = guid

                    if(res.data.cancelled_revision) {
                        cancelledRevisionBulkUploadIds.current.add(id)
                        cancelledRevisionUploadGuids.current.add(guid)
                    }

                    if(shouldStopForCancellation(stateForUpload({guid}), res.data.cancelled_revision)) {
                        clearUploadTracking(guid)
                        return
                    }

                    const closeMessage = message.open({
                        key: `preparing-${id}`,
                        type: 'loading',
                        content: <PreparingUploadMessage count={res.data.count} progress={0}/>,
                        duration: 0
                    })

                    // Failsafe interval in case `ready` message missed
                    const pollStatus = ()=> {
                        if(!lifecycle.beginStatusRequest(id)) return

                        api(`/api/bulk_uploads/${id}/status`).then(res => {

                            console.log(`Checking Bulk Upload ${id} status: `, res.data.status)

                            if(res.data.status == 'ready') onReady(res.data, 'poll', lifecycle)
                            if(res.data.status == 'error') onError(res.data, lifecycle)
                        }).finally(() => lifecycle.finishStatusRequest(id))
                    }

                    const intervalId = setInterval(pollStatus, 3_000)
                    lifecycle.start(id, {intervalId, closeMessage, pollNow: pollStatus})
                }).catch(error => {
                    preparingUploadGuids.current.delete(guid)

                    if(error.response?.status === 409) {
                        clearUploadTracking(guid)
                        return
                    }

                    console.error('Failed to prepare upload:', error)
                })
            }
        })
    }, [fileAdds])

    const onFinish = (asset)=>{
        api.get(`/api/assets/${asset.guid}/set_uploaded`).then(res => {
            const uploadGuid = asset.upload?.guid

            if(shouldStopForCancellation(stateForUpload(asset.upload), asset.allowed_after_cancellation)) {
                clearUploadTracking(uploadGuid)
                return
            }

            if(uploadCounts.current[uploadGuid] > 0) {
                uploadCounts.current[uploadGuid] -= 1
                totalUploads.current = Math.max(0, totalUploads.current - 1);

                if(uploadCounts.current[uploadGuid] === 0) {
                    delete uploadCounts.current[uploadGuid]
                    cancelledRevisionUploadGuids.current.delete(uploadGuid)
                }
            }

            uploadsDispatch({type: 'removeUploadingAsset', assetId: asset.id})

            if(currentUpload?.id === res.data.upload_id) {
                delete res.data.aasm_state // prevent race condition with Sidekiq
                assetsDispatch({type:'updateAsset', asset: res.data})
                editAssetDispatch({type:'increment'})

                // Coalesce completion bursts into one upload-stats refresh.
                refreshUploadStats();
            }
        }).catch(error => {
            if(error.response?.status === 409) {
                clearUploadTracking(asset.upload?.guid)
                return
            }

            console.error('Failed to mark asset uploaded:', error)
        })
    }

    //--------------------------------------
    // Create Evaporate.js Instance For Org:
    //--------------------------------------

    const uploader = useRef();
    window.uploader = uploader;

    useEffect(()=> {
        if(!currentOrg) return;

        const config = {
            logging: false,
            progressIntervalMS: 250,
            signerUrl: `${Config.apiHost}/api/assets/sign`,
            aws_key: Config.awsKey,
            bucket: Config.uploadBucket,
            s3Acceleration: true,
            computeContentMd5: true,
            cryptoMd5Method: (data)=> { btoa(SparkMD5.ArrayBuffer.hash(data, true)) },
            cryptoHexEncodedHash256: sha256,
            signHeaders: {
                'Authorization': `Bearer ${jwt}`,
                'OrganizationId': currentOrg.id
            }
        }

        uploader.current = Evaporate.create(config);

        const handleBeforeunload = (e)=> {
            console.log('totalUploads:')

            if(totalUploads.current == 0) {
                delete e['returnValue'];
                return true;
            }

            e.preventDefault();
            const msg = `${totalUploads.current} ${pluralize('file', totalUploads.current)} remaining to upload, leaving now will lose them.`
            e.returnValue = msg

            return msg
        }

        window.addEventListener('beforeunload', handleBeforeunload);

        return ()=>{
            window.removeEventListener('beforeunload', handleBeforeunload)
        }
    }, [currentOrg?.id])

    const totalPercent = _.sumBy(uploadingAssets, a => a.stats?.totalUploaded || 0) / _.sumBy(uploadingAssets, 'file_size')

    const [paused, setPaused] = useState(false)

    const pause = useCallback((uploadGuid) => {
        const evaporateIds = [...(evaporateIdsByUploadGuid.current[uploadGuid] || [])]
        console.log('Calling pause on Evaporate for upload:', uploadGuid);
        uploader.current.then(ev => {
            Promise.allSettled(evaporateIds.map(id => ev.pause(id))).then(() => {
                console.log('Evaporate upload paused:', uploadGuid)
            });
        });
        setPaused(true);
    }, []);

    const resume = useCallback((uploadGuid) => {
        const evaporateIds = [...(evaporateIdsByUploadGuid.current[uploadGuid] || [])]
        console.log('Calling resume on Evaporate for upload:', uploadGuid);
        uploader.current.then(ev => {
            Promise.allSettled(evaporateIds.map(id => ev.resume(id))).then(() => {
                console.log('Evaporate upload resumed:', uploadGuid)
            });
        });
        setPaused(false);
    }, []);

    const pauseTrackedUploads = useCallback(() => {
        activeUploadGuids(uploadCounts.current, evaporateIdsByUploadGuid.current).forEach(pause)
    }, [pause])

    const resumeTrackedUploads = useCallback(() => {
        activeUploadGuids(uploadCounts.current, evaporateIdsByUploadGuid.current).forEach(resume)
    }, [resume])

    // Sync uploadState from context with Evaporate pause/resume
    useEffect(() => {
        console.log('Upload state changed:', uploadState, 'Paused state:', paused);
        const appliesToTrackedUpload = isUploadTracked(currentUpload?.guid)

        const isCancelledRevision = cancelledRevisionUploadGuids.current.has(currentUpload?.guid)

        if (appliesToTrackedUpload && shouldStopForCancellation(uploadState, isCancelledRevision)) {
            if(!paused) pause(currentUpload.guid)
            clearUploadTracking(currentUpload.guid)
        } else if (appliesToTrackedUpload && uploadState === 'paused' && !paused) {
            console.log('Pausing upload based on context state');
            pause(currentUpload.guid);
        } else if (appliesToTrackedUpload && uploadState === 'active' && paused) {
            console.log('Resuming upload based on context state');
            resume(currentUpload.guid);
        }
    }, [
        uploadState,
        currentUpload?.guid,
        fileAdds,
        uploadingAssets.length,
        paused,
        pause,
        resume,
        isUploadTracked,
        clearUploadTracking
    ]);

    // Stalled detection
    useEffect(() => {
        const stalledCheckInterval = setInterval(() => {
            const now = Date.now();

            uploadingAssets.forEach(asset => {
                const lastProgress = lastProgressTimestamps.current[asset.id];

                // Check if asset is uploading but hasn't made progress
                if (asset.aasm_state === 'uploading' && lastProgress) {
                    const timeSinceProgress = now - lastProgress;

                    if (timeSinceProgress > STALL_TIMEOUT) {
                        console.log(`Asset ${asset.id} stalled - no progress for ${timeSinceProgress}ms`);

                        // Mark as stalled in context
                        uploadsDispatch({type: 'markAssetStalled', assetId: asset.id});

                        // Notify the upload that owns this asset, even if another upload is being viewed
                        if (asset.upload?.id) {
                            api.post(`/api/uploads/${asset.upload.id}/mark_asset_stalled`, {
                                asset_id: asset.id
                            }).then(() => {
                                // Refresh stats after marking as stalled
                                refreshUploadStats();
                            }).catch(err => console.error('Failed to mark asset as stalled:', err));
                        }

                        // Remove from tracking to avoid duplicate notifications
                        delete lastProgressTimestamps.current[asset.id];
                    }
                }
            });
        }, 10000); // Check every 10 seconds

        return () => clearInterval(stalledCheckInterval);
    }, [uploadingAssets]);

    if(!totalPercent || totalPercent == 0 || totalPercent == 1) return '';

    if(isMobile() || quickUpload) return <></>

    return (
        <Popover
            title={'Current Uploads'}
            overlayStyle={{maxWidth:400}}
            content={
                <>
                    {!paused && (
                        <Button onClick={pauseTrackedUploads} icon={<PauseOutlined/>}>
                            Pause {totalUploads.current} {pluralize('Upload', totalUploads.current)}
                        </Button>
                    ) || (
                        <Button onClick={resumeTrackedUploads} icon={<PlayCircleOutlined/>}>
                            Resume {totalUploads.current} {pluralize('Upload', totalUploads.current)}
                        </Button>
                    )}
                    {/*{uploadingAssets.map(asset => (*/}
                    {/*    <div key={asset.id}>*/}
                    {/*        <small>{asset.filename}</small>*/}
                    {/*        <Progress percent={(asset.percent * 100).toFixed()} size="small" status="active" />*/}
                    {/*    </div>*/}
                    {/*))}*/}
                </>
            }
        >
            <Button type={'text'}>
                <Progress
                    type="circle"
                    status={'active'}
                    percent={totalPercent * 100}
                    width={40}
                    format={p => <span style={{color:'white'}}>{p.toFixed()}%</span>}
                />
            </Button>
        </Popover>
    )
}

const PreparingUploadMessage = ({count, progress})=>{
    const {t} = useTranslation();
    return (
        <>
            {count === 1 ? t('preparing-upload-file','Preparing upload of the file') : t('preparing-upload-files','Preparing upload of {{count}} files', {count})}
            {progress < 1 && (
                <>
                    <br/>
                    <Progress percent={Math.round(progress * 100)} status={'active'}/>
                </>
            )}
        </>
    )
}

const ExistingAssetsModal = ({id})=>{
    const {t} = useTranslation();

    const [open, setOpen] = useState()
    const [loading, setLoading] = useState()
    const [filenames, setFilenames] = useState()

    useEffect(() => {
        if(!open) return;

        setLoading(true)
        api(`/api/bulk_uploads/${id}/existing_assets`).then(res => {
            setLoading(false)
            setFilenames(res.data)
        })
    }, [open, id]);

    return (
        <>
            <Button icon={<OrderedListOutlined/>} block onClick={()=> setOpen(true)}>{t('view', 'View')}</Button>
            <Modal
                open={open}
                onCancel={()=> setOpen(false)}
                footer={null}
                title={t('existing-assets','Existing Assets')}
            >
                <List
                    dataSource={filenames}
                    loading={loading}
                    bordered
                    size={'small'}
                    renderItem={filename => <List.Item><FileFilled/> {filename}</List.Item>}
                />
            </Modal>
        </>
    )

}
